import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import inviteService from '../services/inviteService';
import { useAuth } from '../context/AuthContext';
import { isLowEndDevice } from '../hooks/usePortalTrace';
import { rememberInvite, normalizeInviteCode } from '../utils/inviteRef';

/**
 * heyportal.app/i/{code} — where a printed invite card's QR lands (handoff "Portal invite").
 *
 * Black screen, a ring pops in and spins fast — YOU FOUND US. — then the ring takes over the
 * screen and the page is revealed through it: the lockup, the inviter's face in the ring,
 * INVITED BY, WELCOME TO PORTAL., ACCEPT INVITE. Outside theme: #000, white, JetBrains Mono.
 *
 * Timeline (1×): through at 1900 ms, page at 2540 ms. Reduced motion skips the intro and the
 * ripples; a weak device plays the intro once and never loops the ripples. The code is kept in
 * a cookie + localStorage (`utils/inviteRef`) so it survives sign-up and OAuth, and the auth
 * calls send it as `ref`; the server records the referral once, when the account is created.
 *
 * Signed in already (or the inviter themselves): straight to the inviter's page.
 * Invalid / revoked code: the intro and headline stay, INVITED BY and the footer go, the button
 * is plain sign-up.
 *
 * A club's link (Oct 2026) is the same page with the club in the ring: its logo, YOU'RE INVITED
 * TO JOIN, the club's name as the headline, "n MEMBERS", and its about clamped to three lines
 * with SEE MORE / SEE LESS. After sign-up the invitee lands on the club's own join screen.
 */

const MONO = "'JetBrains Mono', Menlo, monospace";
const OUT = 'cubic-bezier(0,0,.2,1)';
const INTO = 'cubic-bezier(.55,0,.7,.2)';
const T_THROUGH = 1900;
const T_PAGE = 2540;

const CSS = `
@keyframes prInvSpin { to { transform: rotate(360deg); } }
@keyframes prInvPop { from { transform: scale(.55); opacity: 0; } 70% { opacity: 1; } to { transform: scale(1); opacity: 1; } }
@keyframes prInvRipple { 0% { transform: scale(1); opacity: .9; } 100% { transform: scale(1.9); opacity: 0; } }
@keyframes prInvRise { from { transform: translateY(14px); opacity: 0; } to { transform: none; opacity: 1; } }
.pr-inv-accept { height: 52px; padding: 0 32px; border: none; border-radius: 999px; background: #fff; color: #000; font-family: ${MONO}; font-size: 13px; font-weight: 800; letter-spacing: .16em; cursor: pointer; transition: background-color 200ms; }
.pr-inv-accept:hover { background: #00FFFF; }
.pr-inv-signin { font-family: ${MONO}; font-size: 11px; letter-spacing: .12em; color: #fff; text-decoration: none; background: none; border: 0; padding: 0; cursor: pointer; }
.pr-inv-signin:hover { color: #00FFFF; }
.pr-inv-about { margin: 0; max-width: 46ch; font-family: ${MONO}; font-size: 13px; line-height: 1.6; color: rgba(255,255,255,.78); white-space: pre-line; overflow-wrap: anywhere; }
.pr-inv-about[data-clamped="true"] { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden; }
.pr-inv-more { margin-top: 10px; font-family: ${MONO}; font-size: 10px; font-weight: 800; letter-spacing: .16em; color: #00FFFF; background: none; border: 0; padding: 0; cursor: pointer; }
.pr-inv-more:hover { color: #fff; }
@media (prefers-reduced-motion: reduce) { .pr-inv-spin { animation: none !important; } }
`;

function prefersReducedMotion() {
  try { return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

const Bracket = ({ style }) => (
  <span aria-hidden="true" style={{ position: 'absolute', width: 22, height: 22, ...style }} />
);

export default function InviteLandingPage() {
  const { code: rawCode } = useParams();
  const code = normalizeInviteCode(rawCode);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [info, setInfo] = useState(null); // { valid, inviter }
  const reduced = useRef(prefersReducedMotion()).current;
  const weak = useRef(isLowEndDevice()).current;
  const [phase, setPhase] = useState(reduced ? 'page' : 'intro');
  const [geo, setGeo] = useState({ ox: 0, oy: 0, size: 208, far: 2000, cover: 12, measured: false });
  const rootRef = useRef(null);
  const ringRef = useRef(null);
  const aboutRef = useRef(null);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [aboutOverflows, setAboutOverflows] = useState(false);

  // The lookup. A bad code is an answer, not an error.
  useEffect(() => {
    let live = true;
    if (!code) { setInfo({ valid: false }); return undefined; }
    inviteService.describe(code).then((r) => {
      if (!live) return;
      const data = r.success ? r.data : { valid: false };
      setInfo(data);
      if (data.valid) rememberInvite({ code, path: data.inviter?.path || null });
    });
    return () => { live = false; };
  }, [code]);

  // Already on Portal: the card's owner, or anyone signed in, lands on the inviter's page.
  useEffect(() => {
    if (authLoading || !isAuthenticated || !info) return;
    navigate(info.valid && info.inviter?.path ? info.inviter.path : '/featured', { replace: true });
  }, [authLoading, isAuthenticated, info, navigate]);

  // The maths: the on-page ring's centre and size, the farthest corner, the scale that covers it.
  const measure = useCallback(() => {
    const root = rootRef.current;
    const ring = ringRef.current;
    if (!root || !ring) return;
    const a = root.getBoundingClientRect();
    const b = ring.getBoundingClientRect();
    const ox = b.left - a.left + b.width / 2;
    const oy = b.top - a.top + b.height / 2;
    const size = b.width || 208;
    const far = Math.hypot(Math.max(ox, a.width - ox), Math.max(oy, a.height - oy));
    setGeo({ ox, oy, size, far, cover: (far / (size * 0.443)) * 1.15, measured: true });
  }, []);

  useLayoutEffect(() => {
    measure();
    const raf = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', measure); };
  }, [measure]);

  // The timeline.
  useEffect(() => {
    if (reduced) return undefined;
    const t1 = setTimeout(() => setPhase('through'), T_THROUGH);
    const t2 = setTimeout(() => setPhase('page'), T_PAGE);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [reduced]);

  const on = phase === 'page';
  const opened = phase !== 'intro';
  const valid = !!info?.valid && !!info.inviter;
  const isClub = valid && info.inviter.kind === 'club';
  const name = valid ? info.inviter.displayName : '';
  const first = name.trim().split(/\s+/)[0] || '';
  const initial = name.trim().charAt(0).toUpperCase();
  const loopRipples = on && !reduced && !weak;
  const about = isClub ? String(info.inviter.about || '').trim() : '';
  const members = isClub ? Number(info.inviter.memberCount) || 0 : 0;

  // SEE MORE appears when the clamp actually hides something: measured where a layout exists,
  // by length where it does not (jsdom), so the toggle never shows under a two-line about.
  useLayoutEffect(() => {
    if (!about || aboutOpen) return undefined;
    const check = () => {
      const el = aboutRef.current;
      const measured = !!el && el.clientHeight > 0 && el.scrollHeight > el.clientHeight + 1;
      setAboutOverflows(measured || (!el?.clientHeight && about.length > 160));
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [about, aboutOpen]);
  const showMore = !!about && (aboutOverflows || aboutOpen);

  const a = (ms, delay) => (on && !reduced ? `prInvRise ${ms}ms ${OUT} ${delay}ms both` : 'none');
  const pop = (delay) => (on && !reduced ? `prInvPop 420ms ${OUT} ${delay}ms both` : 'none');
  const pageOpacity = on ? 1 : 0;
  const { ox, oy, size, far, cover, measured } = geo;
  const half = size / 2;

  const accept = () => navigate(valid ? `/register?ref=${code}` : '/register');
  const signIn = () => navigate(valid ? `/login?ref=${code}` : '/login');

  const ringImg = (fast) => (
    <img src="/brand/portal-ring.png" alt="" className="pr-inv-spin" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', animation: `prInvSpin ${fast ? 1100 : 6000}ms linear infinite` }} draggable={false} />
  );

  return (
    <div ref={rootRef} data-testid="invite-landing" data-phase={phase} style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', background: '#000', color: '#fff', fontFamily: MONO }}>
      <style>{CSS}</style>

      {/* The page, revealed through the ring. */}
      <div
        data-testid="invite-page"
        style={{
          position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
          padding: '24px 24px 28px', boxSizing: 'border-box', gap: 24,
          clipPath: reduced ? 'none' : `circle(${opened ? Math.ceil(far || 2000) + 40 : 0}px at ${ox}px ${oy}px)`,
          transition: opened && !reduced ? `clip-path 640ms ${INTO}` : 'none',
        }}
      >
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'repeating-linear-gradient(0deg, rgba(255,255,255,.05) 0 1px, transparent 1px 4px)' }} />
        <Bracket style={{ left: 20, top: 20, borderLeft: '2px solid rgba(255,255,255,.2)', borderTop: '2px solid rgba(255,255,255,.2)' }} />
        <Bracket style={{ right: 20, top: 20, borderRight: '2px solid rgba(255,255,255,.2)', borderTop: '2px solid rgba(255,255,255,.2)' }} />
        <Bracket style={{ left: 20, bottom: 20, borderLeft: '2px solid rgba(255,255,255,.2)', borderBottom: '2px solid rgba(255,255,255,.2)' }} />
        <Bracket style={{ right: 20, bottom: 20, borderRight: '2px solid rgba(255,255,255,.2)', borderBottom: '2px solid rgba(255,255,255,.2)' }} />

        <img src="/brand/portal-lockup-on-black.png" alt="Portal" style={{ position: 'relative', flex: 'none', width: 120, height: 'auto', opacity: pageOpacity, animation: a(320, 260) }} draggable={false} />

        <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
          <div ref={ringRef} data-testid="invite-ring" style={{ position: 'relative', width: 'clamp(140px, 26vh, 208px)', height: 'clamp(140px, 26vh, 208px)', flex: 'none' }}>
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #00FFFF', opacity: 0, animation: loopRipples ? `prInvRipple 2400ms ${OUT} 900ms infinite` : 'none' }} />
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #00FFFF', opacity: 0, animation: loopRipples ? `prInvRipple 2400ms ${OUT} 2100ms infinite` : 'none' }} />
            <span style={{ position: 'absolute', inset: 0, opacity: pageOpacity, animation: pop(0) }}>{ringImg(false)}</span>
            <span
              data-testid="invite-avatar"
              style={{ position: 'absolute', inset: '5.5%', borderRadius: '50%', overflow: 'hidden', background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'clamp(44px, 8vh, 64px)', fontWeight: 700, color: '#fff', opacity: pageOpacity, animation: pop(120) }}
            >
              {valid && info.inviter.avatarUrl
                ? <img src={info.inviter.avatarUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} draggable={false} />
                : (valid ? initial : '')}
            </span>
          </div>

          {valid && (
            <span data-testid="invite-by" style={{ marginTop: 'clamp(20px, 4vh, 36px)', fontSize: 12, fontWeight: 700, letterSpacing: '.16em', color: 'rgba(255,255,255,.7)', opacity: pageOpacity, animation: a(320, 260) }}>
              {isClub ? "YOU'RE INVITED TO JOIN" : `INVITED BY ${name.toUpperCase()}`}
            </span>
          )}

          <h1 style={{ margin: `${valid ? 16 : 'clamp(20px, 4vh, 36px)'} 0 0`, maxWidth: isClub ? '22ch' : '17ch', fontFamily: MONO, fontSize: 'clamp(22px, min(4.6vw, 5.4vh), 44px)', lineHeight: 1.15, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', textWrap: 'balance', color: '#fff', opacity: pageOpacity, animation: a(380, 360) }}>
            {isClub ? name : 'Welcome to Portal.'}
          </h1>

          {isClub && members > 0 && (
            <span data-testid="invite-members" style={{ marginTop: 10, fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: 'rgba(255,255,255,.55)', opacity: pageOpacity, animation: a(320, 420) }}>
              {members} {members === 1 ? 'MEMBER' : 'MEMBERS'}
            </span>
          )}

          {isClub && about && (
            <div data-testid="invite-about-wrap" style={{ marginTop: 'clamp(16px, 3vh, 24px)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 8px', opacity: pageOpacity, animation: a(380, 460) }}>
              <p ref={aboutRef} className="pr-inv-about" data-testid="invite-about" data-clamped={!aboutOpen} data-expanded={aboutOpen}>
                {about}
              </p>
              {showMore && (
                <button type="button" className="pr-inv-more" data-testid="invite-about-toggle" aria-expanded={aboutOpen} onClick={() => setAboutOpen((v) => !v)}>
                  {aboutOpen ? 'SEE LESS' : 'SEE MORE'}
                </button>
              )}
            </div>
          )}

          <div style={{ marginTop: 'clamp(24px, 5vh, 40px)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, opacity: pageOpacity, animation: a(380, 520) }}>
            <button type="button" className="pr-inv-accept" onClick={accept} data-testid="invite-accept">ACCEPT INVITE</button>
            <button type="button" className="pr-inv-signin" onClick={signIn} data-testid="invite-signin">ALREADY ON PORTAL? SIGN IN</button>
          </div>
        </div>

        {valid && (
          <span data-testid="invite-foot" style={{ position: 'relative', flex: 'none', textAlign: 'center', fontSize: 10, letterSpacing: '.12em', color: 'rgba(255,255,255,.55)', opacity: pageOpacity, animation: a(380, 700) }}>
            YOU'LL LAND ON {(isClub ? name : first).toUpperCase()}'S PAGE AFTER SIGN-UP.
          </span>
        )}
      </div>

      {/* The intro: a ring at the exact centre and size of the on-page ring, then it takes the screen. */}
      {!reduced && phase !== 'page' && (
        <div aria-hidden="true" data-testid="invite-intro" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: measured ? 1 : 0 }}>
          <div style={{ position: 'absolute', left: ox - half, top: oy - half, width: size, height: size }}>
            {[350, 850, 1350].map((d) => (
              <span key={d} style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '2px solid #00FFFF', opacity: 0, animation: `prInvRipple 1500ms ${OUT} ${d}ms ${weak ? 1 : 'infinite'}` }} />
            ))}
            <div style={{ position: 'absolute', inset: 0, transform: `scale(${opened ? cover : 1})`, transition: opened ? `transform 640ms ${INTO}, opacity 192ms linear 448ms` : 'none', opacity: opened ? 0 : 1 }}>
              <span style={{ position: 'absolute', inset: 0, animation: `prInvPop 420ms ${OUT} both` }}>{ringImg(true)}</span>
            </div>
          </div>
          <span style={{ position: 'absolute', left: 0, right: 0, top: oy + half + 32, textAlign: 'center', fontSize: 15, fontWeight: 700, letterSpacing: '.14em', color: '#fff', opacity: opened ? 0 : 1, transition: 'opacity 200ms linear', animation: `prInvRise 320ms ${OUT} 380ms both` }}>
            YOU FOUND US.
          </span>
        </div>
      )}
    </div>
  );
}
